import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Container, Overline } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { ApiError, apiClient } from '@/lib/apiClient'
import {
  accessToken,
  fetchMe,
  login,
  logout,
  readSession,
  type MeResponse,
  type StaffSession,
} from '@/lib/staffSession'

interface SessionControl {
  session: StaffSession
  signOut: () => void
  /** L'édition choisie à la main, quand il y en a plusieurs. */
  selectedEventId: string | null
  selectEvent: (eventId: string | null) => void
}

/** Une édition telle que le back-office la liste (`GET /admin/events`). */
export interface AdminEditionSummary {
  id: string
  slug: string
  edition_year: number
  status: 'draft' | 'published' | 'closed' | 'archived'
  name_i18n: Record<string, string>
  timezone: string
  currency: string
  starts_at: string
}

/** Ce que les écrans d'administration lisent de l'édition en cours. */
export interface AdminEvent {
  id: string
  slug: string
  name: string
  currency: string
  timezone: string
  status: AdminEditionSummary['status'] | null
}

const SELECTED_EVENT = 'masked-night.admin-event'

function readSelected(): string | null {
  try {
    return localStorage.getItem(SELECTED_EVENT)
  } catch {
    return null
  }
}

const SessionContext = createContext<SessionControl | null>(null)

export function useAdminSession(): SessionControl {
  const value = useContext(SessionContext)
  if (!value) {
    // Ne peut arriver qu'en cas d'erreur de montage : un écran d'admin rendu
    // hors de `AdminGate`. Mieux vaut le dire franchement qu'afficher une
    // page à moitié fonctionnelle.
    throw new Error('useAdminSession doit être utilisé sous <AdminGate>.')
  }
  return value
}

/**
 * Qui est connecté, sur quelle édition, avec quelles permissions.
 *
 * `permissions` ne sert qu'à **masquer** ce qui serait de toute façon refusé :
 * chaque endpoint revérifie. Cacher un bouton est un confort, pas une
 * sécurité — un back-office qui ferait confiance à cette liste laisserait
 * passer n'importe quel appel forgé à la main.
 */
export function useAdminContext() {
  const { i18n } = useTranslation()
  const control = useContext(SessionContext)
  const eventQuery = usePublicEvent()

  // L'édition se lisait sur l'API publique, qui ne connaît que les éditions
  // PUBLIÉES : une édition en brouillon, ou close au lendemain de la soirée,
  // donnait un back-office vide — y compris l'écran du bilan.
  //
  // `/admin/events` exige un rôle GLOBAL : un compte dont les rôles sont tous
  // limités à une édition en brouillon recevait un 403, retombait sur
  // l'édition publiée — où il n'a aucun droit — et voyait un back-office
  // vide. `/auth/me/editions` rend à chacun les éditions où il tient un rôle
  // (toutes, pour qui lit les éditions globalement).
  const editionsQuery = useQuery({
    queryKey: ['admin-editions'],
    queryFn: async () =>
      apiClient.get<AdminEditionSummary[]>('/auth/me/editions', {
        token: await accessToken(),
      }),
    retry: false,
    staleTime: 60_000,
  })
  const editions = editionsQuery.data ?? []
  const selected = control?.selectedEventId ?? null

  const chosen =
    (selected ? editions.find((edition) => edition.id === selected) : undefined) ??
    (eventQuery.data ? editions.find((edition) => edition.id === eventQuery.data!.id) : undefined) ??
    // Sans édition publiée — ou publiée, mais hors de portée de ce compte :
    // la plus récente de ses éditions qui n'est pas archivée.
    editions.find((edition) => edition.status !== 'archived')

  const eventId = chosen?.id ?? (selected && editionsQuery.isPending ? undefined : eventQuery.data?.id)

  const event: AdminEvent | undefined = chosen
    ? {
        id: chosen.id,
        slug: chosen.slug,
        name:
          chosen.name_i18n[i18n.language] ||
          Object.values(chosen.name_i18n).find(Boolean) ||
          chosen.slug,
        currency: chosen.currency,
        timezone: chosen.timezone,
        status: chosen.status,
      }
    : eventQuery.data && eventQuery.data.id === eventId
      ? {
          id: eventQuery.data.id,
          slug: eventQuery.data.slug,
          name: eventQuery.data.name,
          currency: eventQuery.data.currency,
          timezone: eventQuery.data.timezone,
          status: null,
        }
      : undefined

  const meQuery = useQuery({
    queryKey: ['admin-me', eventId],
    queryFn: () => fetchMe(eventId),
    enabled: Boolean(eventId),
    retry: false,
  })

  return {
    event,
    eventId,
    editions,
    selectEvent: control?.selectEvent ?? (() => undefined),
    me: meQuery.data,
    isPending: (eventQuery.isPending && editionsQuery.isPending) || meQuery.isPending,
    can: (permission: string) => can(meQuery.data, permission),
  }
}

function can(me: MeResponse | undefined, permission: string): boolean {
  return Boolean(me?.permissions.includes(permission))
}

/**
 * Porte d'entrée du back-office.
 *
 * Tant qu'aucune session n'est ouverte, on n'affiche que le formulaire : pas
 * de squelette d'interface derrière, qui laisserait croire que les données
 * sont en train d'arriver.
 */
export function AdminGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StaffSession | null>(() => readSession())
  const [selectedEventId, setSelectedEventId] = useState<string | null>(() => readSelected())

  if (!session) return <AdminLogin onSuccess={setSession} />

  return (
    <SessionContext.Provider
      value={{
        session,
        signOut: () => {
          // La session locale part d'abord (dans `logout`) : même si le
          // serveur ne répond pas, l'écran ne doit pas rester ouvert sur une
          // tablette posée. Le serveur révoque ensuite le jeton de
          // rafraîchissement — sans cela, « Se déconnecter » laissait une
          // session valide derrière soi.
          void logout()
          setSession(null)
        },
        selectedEventId,
        selectEvent: (eventId) => {
          try {
            if (eventId) localStorage.setItem(SELECTED_EVENT, eventId)
            else localStorage.removeItem(SELECTED_EVENT)
          } catch {
            // Choix non mémorisé : il vaut pour cet onglet, c'est déjà ça.
          }
          setSelectedEventId(eventId)
        },
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

function AdminLogin({ onSuccess }: { onSuccess: (session: StaffSession) => void }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    document.title = t('admin.title')
  }, [t])

  return (
    <Container className="flex min-h-dvh max-w-md flex-col justify-center">
      {/* La langue se choisit avant même de se connecter : le formulaire
          est la première chose qu'un nouveau membre de l'équipe doit lire. */}
      <div className="mb-8 flex items-center justify-between gap-4">
        <img src="/leyamo.png" alt="" className="h-14 w-14 rounded-full" />
        <LanguageSwitcher />
      </div>
      <Overline>{t('admin.overline')}</Overline>
      <h1 className="mt-3 font-display text-display-md text-content-primary">
        {t('admin.signIn')}
      </h1>

      <form
        className="mt-8 space-y-4"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault()
          setBusy(true)
          setError(null)
          login(email, password)
            .then(onSuccess)
            .catch((cause: unknown) => {
              setError(cause instanceof ApiError ? cause.message : t('admin.signInFailed'))
            })
            .finally(() => setBusy(false))
        }}
      >
        <label className="block text-caption text-content-muted">
          {t('admin.email')}
          <input
            type="email"
            value={email}
            onChange={(changeEvent) => setEmail(changeEvent.target.value)}
            required
            autoComplete="username"
            className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-3 text-content-primary"
          />
        </label>
        <label className="block text-caption text-content-muted">
          {t('admin.password')}
          <input
            type="password"
            value={password}
            onChange={(changeEvent) => setPassword(changeEvent.target.value)}
            required
            autoComplete="current-password"
            className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-3 text-content-primary"
          />
        </label>

        {error ? <p className="text-sm text-state-danger">{error}</p> : null}

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? t('state.loading') : t('admin.signIn')}
        </Button>
      </form>
    </Container>
  )
}
